import { defineMessages, IntlShape, MessageDescriptor } from 'react-intl';

/**
 * The words for the angle and the facts of a pitch (design outreach f1). P4 answers an angle key
 * and the input paths it used (`target.similarArtistsAdded[0]`); the review card shows words.
 */
const labels = defineMessages({
  angleSimilarArtist: { id: 'app.outreach.review.angle.similarArtist', defaultMessage: 'A similar artist they added' },
  anglePlaylistFit: { id: 'app.outreach.review.angle.playlistFit', defaultMessage: 'The song fits what they play' },
  angleStory: { id: 'app.outreach.review.angle.story', defaultMessage: 'The story of the song' },
  angleLocalScene: { id: 'app.outreach.review.angle.localScene', defaultMessage: 'Your local scene' },
  angleMomentum: { id: 'app.outreach.review.angle.momentum', defaultMessage: 'Your momentum' },
  angleFollowUp: {
    id: 'app.outreach.review.angle.followUpOnPlacement',
    defaultMessage: 'A thank-you for an earlier placement',
  },
  factSimilarArtists: {
    id: 'app.outreach.review.fact.similarArtists',
    defaultMessage: 'The similar artists they added',
  },
  factRules: { id: 'app.outreach.review.fact.rules', defaultMessage: 'Their submission rules' },
  factInvitation: { id: 'app.outreach.review.fact.invitation', defaultMessage: 'Their call for submissions' },
  factContactName: { id: 'app.outreach.review.fact.contactName', defaultMessage: 'Their name' },
  factTarget: { id: 'app.outreach.review.fact.target', defaultMessage: 'Their playlist, blog or show' },
  factStory: { id: 'app.outreach.review.fact.story', defaultMessage: 'The story of the song' },
  factSound: { id: 'app.outreach.review.fact.sound', defaultMessage: 'Your genres and moods' },
  factRelease: { id: 'app.outreach.review.fact.release', defaultMessage: 'The release' },
  factProof: { id: 'app.outreach.review.fact.proof', defaultMessage: 'Your numbers' },
  factWords: { id: 'app.outreach.review.fact.words', defaultMessage: 'Your words' },
  factVoice: { id: 'app.outreach.review.fact.voice', defaultMessage: 'Your voice sample' },
  factOther: { id: 'app.outreach.review.fact.other', defaultMessage: 'Another fact from your profile' },
});

const ANGLES: Record<string, MessageDescriptor> = {
  similarArtist: labels.angleSimilarArtist,
  playlistFit: labels.anglePlaylistFit,
  story: labels.angleStory,
  localScene: labels.angleLocalScene,
  momentum: labels.angleMomentum,
  followUpOnPlacement: labels.angleFollowUp,
};

const FACTS: Record<string, MessageDescriptor> = {
  'target.similarArtistsAdded': labels.factSimilarArtists,
  'target.rules': labels.factRules,
  'target.invitation': labels.factInvitation,
  'target.contactName': labels.factContactName,
  'target.curatorFirstName': labels.factContactName,
  target: labels.factTarget,
  'profile.releaseStory': labels.factStory,
  profile: labels.factSound,
  release: labels.factRelease,
  proof: labels.factProof,
  userInstructions: labels.factWords,
  voiceSample: labels.factVoice,
};

/** The words for an angle key; an unknown key shows as it is. */
export function angleText(intl: IntlShape, angle: string): string {
  const message = ANGLES[angle];
  return message === undefined ? angle : intl.formatMessage(message);
}

function factMessage(key: string): MessageDescriptor {
  // "target.similarArtistsAdded[0].name" → "target.similarArtistsAdded", then "target".
  const parts = key.replace(/\[\d+\]/g, '').split('.');
  const exact = FACTS[parts.slice(0, 2).join('.')];
  return exact ?? FACTS[parts[0] ?? ''] ?? labels.factOther;
}

/** True for an input path such as `profile.releaseStory`; a sentence has a space. */
const isInputPath = (key: string) => !/\s/.test(key);

/** The facts of a pitch in words, each one once. A fact that is already a sentence stays as it is. */
export function factTexts(intl: IntlShape, keys: string[]): string[] {
  return Array.from(new Set(keys.map((key) => (isInputPath(key) ? intl.formatMessage(factMessage(key)) : key))));
}
